import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMockMode } from '@/api';
import {
  type DroomBooking,
  type DroomSlotCell,
  confirmDroomBooking,
  getDroomBooking,
  getDroomSlots,
  holdDroomSlot,
  requestDroomReschedule,
} from '@/api/booking';
import { ApiError } from '@/api/client';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { customerTimeZone, formatSlotStamp } from '../slotTime';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';

/**
 * SCREEN 01b · SCHEDULE — the now-vs-later fork (SPEC-07 §9 / F-CA-09, the
 * customer leg of the live droom booking seam). A customer with a confirmed
 * booking sees their appointment (their OWN timezone first); everyone else
 * chooses "continue now" (the walk-in default — one tap, nothing else) or
 * picks a time from the SAFE grid (slotStart/slotEnd/bookable only — agent
 * identities and tenant capacity never reach this wire, by design).
 *
 * Honesty rules: 409 slot-taken re-picks calmly from the refreshed truth; the
 * hold's ~5-minute TTL is stated; confirm replays its own idempotency key;
 * a session past the bookable band renders "your session has moved on" and
 * continues — never an error tone for a state the customer cannot fix.
 */
export function Schedule({ tenant }: StepProps): JSX.Element {
  const { t, locale } = useT();
  const real = !isMockMode();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);

  type Phase =
    | { kind: 'loading' }
    | { kind: 'choice' }
    | { kind: 'picking'; cells: DroomSlotCell[]; note: string | null }
    | { kind: 'held'; booking: DroomBooking }
    | { kind: 'booked'; booking: DroomBooking }
    | { kind: 'moved-on' };
  const [phase, setPhase] = useState<Phase>({ kind: real ? 'loading' : 'choice' });
  const [busy, setBusy] = useState(false);
  const customerTz = useMemo(() => customerTimeZone(), []);
  // One idempotency key per held booking — replayed verbatim on retry (§4.3).
  const idemKey = useRef<string | null>(null);

  useEffect(() => {
    if (!real) return;
    let cancelled = false;
    getDroomBooking(sessionId)
      .then((b) => {
        if (cancelled) return;
        if (b.state === 'BOOKED' || b.state === 'REMINDED') {
          setPhase({ kind: 'booked', booking: b });
        } else if (b.state === 'HELD') {
          setPhase({ kind: 'held', booking: b });
        } else {
          setPhase({ kind: 'choice' });
        }
      })
      .catch(() => {
        if (!cancelled) setPhase({ kind: 'choice' }); // 404 = no booking yet — the walk-in default
      });
    return () => {
      cancelled = true;
    };
  }, [real, sessionId]);

  const loadGrid = useCallback(
    async (note: string | null) => {
      setBusy(true);
      try {
        const from = new Date();
        const to = new Date(from.getTime() + 3 * 24 * 3_600_000); // 3 days — compact on a phone
        const grid = await getDroomSlots(sessionId, from.toISOString(), to.toISOString());
        setPhase({ kind: 'picking', cells: grid.cells.filter((c) => c.bookable), note });
      } catch (err) {
        if (err instanceof ApiError && err.status === 422) setPhase({ kind: 'moved-on' });
        else setPhase({ kind: 'picking', cells: [], note: 'droom.schedule.note.loadfail' });
      } finally {
        setBusy(false);
      }
    },
    [sessionId],
  );

  const pick = useCallback(
    async (cell: DroomSlotCell) => {
      setBusy(true);
      try {
        const held = await holdDroomSlot(sessionId, cell.slotStart, customerTz);
        idemKey.current = `droom-${held.id ?? cell.slotStart}`;
        setPhase({ kind: 'held', booking: held });
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          await loadGrid('droom.schedule.note.taken');
        } else if (err instanceof ApiError && err.status === 422) {
          setPhase({ kind: 'moved-on' });
        } else {
          await loadGrid('droom.schedule.note.holdfail');
        }
      } finally {
        setBusy(false);
      }
    },
    [sessionId, customerTz, loadGrid],
  );

  const confirm = useCallback(async () => {
    setBusy(true);
    try {
      const booked = await confirmDroomBooking(sessionId, idemKey.current ?? 'droom-confirm');
      setPhase({ kind: 'booked', booking: booked });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await loadGrid('droom.schedule.note.lapsed');
      } else {
        await loadGrid('droom.schedule.note.confirmfail');
      }
    } finally {
      setBusy(false);
    }
  }, [sessionId, loadGrid]);

  const reschedule = useCallback(async () => {
    setBusy(true);
    try {
      await requestDroomReschedule(sessionId, 'customer asked to move the appointment');
      await loadGrid('droom.schedule.note.repick');
    } catch {
      await loadGrid(null);
    } finally {
      setBusy(false);
    }
  }, [sessionId, loadGrid]);

  // The appointment stamp now lives in flow/slotTime.ts — the SAME rendering the
  // scheduled-arrival screens use, so a customer reads one time when they book and
  // the identical one when they arrive early (CD32). Options are unchanged; a slot
  // that will not parse falls back to the served value instead of "Invalid Date".
  const stamp = useCallback(
    (iso: string) => formatSlotStamp(iso, locale, customerTz) ?? iso,
    [customerTz, locale],
  );

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.schedule.subtitle')} />
      <main className="center-col" style={{ padding: 16 }} aria-label={t('droom.schedule.aria')}>
        {phase.kind === 'loading' ? (
          <p className="sub">{t('droom.schedule.checking')}</p>
        ) : phase.kind === 'booked' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.schedule.booked.title')}</h2>
            <p className="h-title">{stamp(phase.booking.slotStart)}</p>
            <p className="sub">{t('droom.schedule.booked.sub', { tz: customerTz })}</p>
            <Button onClick={() => advance()}>{t('droom.schedule.booked.continue')}</Button>
            <Button variant="ghost" disabled={busy} onClick={() => void reschedule()}>
              {t('droom.schedule.booked.move')}
            </Button>
          </section>
        ) : phase.kind === 'held' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.schedule.held.title')}</h2>
            <p className="h-title">{stamp(phase.booking.slotStart)}</p>
            <p className="sub">{t('droom.schedule.held.sub')}</p>
            <Button disabled={busy} onClick={() => void confirm()}>
              {busy ? t('droom.schedule.held.confirming') : t('droom.schedule.held.confirm')}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => void loadGrid(null)}>
              {t('droom.schedule.held.different')}
            </Button>
          </section>
        ) : phase.kind === 'picking' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.schedule.pick.title')}</h2>
            {phase.note ? (
              <p className="sub" role="status">
                {t(phase.note)}
              </p>
            ) : null}
            {phase.cells.length === 0 ? (
              <>
                <p className="sub">{t('droom.schedule.pick.empty')}</p>
                <Button onClick={() => advance()}>{t('droom.schedule.pick.now')}</Button>
              </>
            ) : (
              <div className="slot-pick-list" role="list">
                {phase.cells.slice(0, 48).map((c) => (
                  <button
                    key={c.slotStart}
                    type="button"
                    className="slot-pick"
                    disabled={busy}
                    onClick={() => void pick(c)}
                  >
                    {stamp(c.slotStart)}
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" onClick={() => advance()}>
              {t('droom.schedule.pick.skip')}
            </Button>
          </section>
        ) : phase.kind === 'moved-on' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.schedule.moved.title')}</h2>
            <p className="sub">{t('droom.schedule.moved.sub')}</p>
            <Button onClick={() => advance()}>{t('droom.action.continue')}</Button>
          </section>
        ) : (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.schedule.choice.title')}</h2>
            <p className="sub">{t('droom.schedule.choice.sub')}</p>
            <Button onClick={() => advance()}>{t('droom.schedule.pick.now')}</Button>
            {real ? (
              <Button variant="ghost" disabled={busy} onClick={() => void loadGrid(null)}>
                {t('droom.schedule.choice.pick')}
              </Button>
            ) : null}
          </section>
        )}
      </main>
    </div>
  );
}
